import { useState, useCallback, useRef, useEffect } from "react";
import { PenTool, Upload, Download, Trash2, FileText, Loader2, Type, ImageIcon, Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "Is this PDF signing tool free?",
    answer: "Yes, completely free with no signup required. Sign unlimited PDFs online."
  },
  {
    question: "How can I create my signature?",
    answer: "You have three options: draw with your mouse/touchscreen, type your name and choose from 5 elegant font styles, or upload an image of your signature."
  },
  {
    question: "Is my signature legally valid?",
    answer: "This tool adds a visual signature to your PDF. For legally binding electronic signatures, consider dedicated e-signature services."
  },
  {
    question: "Can I position my signature anywhere?",
    answer: "Yes, use the position sliders to place your signature exactly where you need it on any page."
  },
  {
    question: "Are my documents secure?",
    answer: "Yes, all files are automatically deleted after 30 minutes. We never store or access your documents."
  }
];

// Signature font styles
const signatureStyles = [
  { id: "elegant", name: "Elegant", fontFamily: "'Dancing Script', cursive", fontWeight: "400" },
  { id: "formal", name: "Formal", fontFamily: "'Great Vibes', cursive", fontWeight: "400" },
  { id: "bold", name: "Bold", fontFamily: "'Pacifico', cursive", fontWeight: "400" },
  { id: "classic", name: "Classic", fontFamily: "'Allura', cursive", fontWeight: "400" },
  { id: "modern", name: "Modern", fontFamily: "'Sacramento', cursive", fontWeight: "400" },
];

const PdfSignPage = () => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(1);
  const [signatureType, setSignatureType] = useState("draw");
  const [signatureData, setSignatureData] = useState("");
  const [typedName, setTypedName] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("elegant");
  const [signaturePosition, setSignaturePosition] = useState({ x: 100, y: 600 });
  const [signatureSize, setSignatureSize] = useState({ width: 200, height: 80 });
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Load Google Fonts for signature styles
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Allura&family=Sacramento&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
      await loadPreview(droppedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
      await loadPreview(selectedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const loadPreview = async (pdfFile) => {
    setIsLoadingPreview(true);
    const formData = new FormData();
    formData.append("file", pdfFile);

    try {
      const response = await axios.post(`${API}/pdf-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setPages(response.data.pages);
        setSelectedPage(1);
      }
    } catch (error) {
      toast.error("Failed to load preview");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Canvas drawing setup
  useEffect(() => {
    if (signatureType === "draw" && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#000066";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [signatureType]);

  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!canvasRef.current) return;
    setIsDrawing(false);
    setSignatureData(canvasRef.current.toDataURL("image/png"));
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureData("");
  };

  const handleImageUpload = (e) => {
    const imgFile = e.target.files[0];
    if (imgFile && imgFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSignatureData(event.target.result);
      };
      reader.readAsDataURL(imgFile);
    }
  };

  const handleSign = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    let sigData = signatureData;
    if (signatureType === "text") {
      if (!typedName.trim()) {
        toast.error("Please enter your name");
        return;
      }
      // Include style info with the name
      const style = signatureStyles.find(s => s.id === selectedStyle);
      sigData = JSON.stringify({ name: typedName, style: style.fontFamily });
    } else if (!sigData) {
      toast.error("Please create or upload a signature");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("signature_data", sigData);
    formData.append("signature_type", signatureType);
    formData.append("page", selectedPage);
    formData.append("x", signaturePosition.x);
    formData.append("y", signaturePosition.y);
    formData.append("width", signatureSize.width);
    formData.append("height", signatureSize.height);

    try {
      const response = await axios.post(`${API}/pdf-sign`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signing failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "signed.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPages([]);
    setSignatureData("");
    setTypedName("");
  };

  const getCurrentStyle = () => signatureStyles.find(s => s.id === selectedStyle);

  return (
    <>
      <SEOHead
        title="Sign PDF Online Free | Add Signature to PDF"
        description="Sign PDF documents online free. Draw, type, or upload your signature. Multiple signature styles. No signup required. Secure and private."
        canonicalPath="/pdf-sign"
        keywords="sign PDF, add signature to PDF, PDF signature free, electronic signature, e-sign PDF online"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
                <PenTool className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Sign PDF Online Free
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Add your signature to any PDF document. Draw with your mouse, type your name with elegant fonts, 
              or upload a signature image. No signup required.
            </p>
          </div>

        {/* Upload Area */}
        {!result && pages.length === 0 && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {isLoadingPreview ? (
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-muted-foreground" />
                  <p className="font-medium">Loading preview...</p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Signature Editor */}
        {!result && pages.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fade-in-up">
            {/* Left: PDF Preview */}
            <div className="space-y-4">
              <h3 className="font-medium">Select Page & Position</h3>
              <div className="border border-border p-4 bg-secondary">
                <img
                  src={`${API}/download/${pages[selectedPage - 1]?.thumbnail}`}
                  alt={`Page ${selectedPage}`}
                  className="w-full h-auto"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {pages.map((page, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedPage(idx + 1)}
                    className={`w-12 h-16 border-2 overflow-hidden ${
                      selectedPage === idx + 1 ? "border-[#FF3B30]" : "border-border"
                    }`}
                  >
                    <img
                      src={`${API}/download/${page.thumbnail}`}
                      alt={`Page ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Position X: {signaturePosition.x}px</Label>
                  <Slider
                    value={[signaturePosition.x]}
                    onValueChange={([v]) => setSignaturePosition(p => ({ ...p, x: v }))}
                    max={500}
                    min={0}
                  />
                </div>
                <div>
                  <Label>Position Y: {signaturePosition.y}px</Label>
                  <Slider
                    value={[signaturePosition.y]}
                    onValueChange={([v]) => setSignaturePosition(p => ({ ...p, y: v }))}
                    max={800}
                    min={0}
                  />
                </div>
                <div>
                  <Label>Width: {signatureSize.width}px</Label>
                  <Slider
                    value={[signatureSize.width]}
                    onValueChange={([v]) => setSignatureSize(p => ({ ...p, width: v }))}
                    max={400}
                    min={50}
                  />
                </div>
              </div>
            </div>

            {/* Right: Signature Creation */}
            <div className="space-y-4">
              <h3 className="font-medium">Create Signature</h3>
              
              <Tabs value={signatureType} onValueChange={setSignatureType}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="draw" className="flex items-center gap-1">
                    <PenTool className="w-4 h-4" /> Draw
                  </TabsTrigger>
                  <TabsTrigger value="image" className="flex items-center gap-1">
                    <ImageIcon className="w-4 h-4" /> Upload
                  </TabsTrigger>
                  <TabsTrigger value="text" className="flex items-center gap-1">
                    <Type className="w-4 h-4" /> Type
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="draw" className="space-y-4">
                  <div className="border-2 border-dashed border-border bg-white relative">
                    <canvas
                      ref={canvasRef}
                      width={400}
                      height={150}
                      className="w-full cursor-crosshair touch-none"
                      style={{ display: 'block' }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearCanvas}
                    className="rounded-sm"
                  >
                    <Eraser className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </TabsContent>

                <TabsContent value="image" className="space-y-4">
                  <div
                    className="border-2 border-dashed border-border p-8 text-center cursor-pointer hover:bg-secondary"
                    onClick={() => document.getElementById("sig-image").click()}
                  >
                    <input
                      id="sig-image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    {signatureData && signatureType === "image" ? (
                      <img src={signatureData} alt="Signature" className="max-h-32 mx-auto" />
                    ) : (
                      <p className="text-muted-foreground">Click to upload signature image</p>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="text" className="space-y-4">
                  <Input
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Type your name"
                    className="text-lg h-12"
                  />
                  
                  {typedName && (
                    <>
                      <Label className="text-sm text-muted-foreground">Choose a style:</Label>
                      <div className="grid grid-cols-1 gap-2">
                        {signatureStyles.map((style) => (
                          <button
                            key={style.id}
                            onClick={() => setSelectedStyle(style.id)}
                            className={`border-2 p-4 text-center transition-all ${
                              selectedStyle === style.id
                                ? "border-[#FF3B30] bg-red-50"
                                : "border-border hover:border-gray-400"
                            }`}
                          >
                            <span
                              className="text-2xl text-blue-900"
                              style={{ fontFamily: style.fontFamily }}
                            >
                              {typedName}
                            </span>
                            <span className="block text-xs text-muted-foreground mt-1">
                              {style.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </TabsContent>
              </Tabs>

              <div className="flex gap-4 pt-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="flex-1 rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSign}
                  disabled={isProcessing}
                  className="flex-1 bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing...
                    </>
                  ) : (
                    <>
                      <PenTool className="w-4 h-4 mr-2" />
                      Sign PDF
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <PenTool className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                PDF Signed Successfully
              </h2>
              <p className="text-muted-foreground mb-6">
                Your signature has been added to the document
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-sm"
                  data-testid="reset-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sign Another
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                  data-testid="download-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Signed PDF
                </Button>
              </div>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="pdf-sign" content={toolContent["pdf-sign"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="pdf-sign" />
      </div>
    </div>
    </>
  );
};

export default PdfSignPage;
